import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApplicantService } from '@core/services/auth-user/applicant.service';
import { MixpanelService } from '@core/services/mixpanel.service';
import { SpecialHandlingService } from '@core/services/special-handling.service';
import { SpinnerService } from '@core/services/spinner.service';
import { StatusService } from '@core/services/status.service';
import { TranslationService } from '@core/services/translation.service';
import { Applicant } from '@core/typings/applicant.typing';
import { ApplicationApplicantFile, ApplyPageApplication, BaseApplication, NominationForm, Nominee, RoutingOrEligibilityChangesToSave, SaveApplication, SaveApplicationResponse, SubmitEligibility } from '@core/typings/application.typing';
import { ProcessingTypes } from '@core/typings/payment.typing';
import { OrgInfoForPDF } from '@core/typings/pdf.typing';
import { ProgramApplicantType, ProgramDetail } from '@core/typings/program.typing';
import { ApplicationStatuses } from '@core/typings/status.typing';
import { ProgramTranslationMap } from "@core/typings/translation.typing";
import { ReferenceFieldsUI } from '@core/typings/ui/reference-fields.typing';
import { ContactSupport } from '@core/typings/ui/support.typing';
import { AddOrganizationService } from '@features/add-organization/add-organization.service';
import { ApplicantCommunicationService } from '@features/applicant-communications/services/applicant-communications.service';
import { CommunicationForApplicant } from '@features/applicant-communications/typings/applicant-communications.typing';
import { ApplicationDownloadService } from '@features/application-download/application-download.service';
import { ApplicationEligibilityService } from '@features/application-eligibility/application-eligibility.service';
import { ApplicationEligibility } from '@features/application-eligibility/application-eligibility.typing';
import { ApplicationFormService } from '@features/application-forms/services/application-forms.service';
import { ApplicationAttachmentService } from '@features/application-view/application-attachments/application-attachments.service';
import { AwardService } from '@features/awards/award.service';
import { MyAward } from '@features/awards/typings/award.typing';
import { ClientSettingsService } from '@features/client-settings/client-settings.service';
import { BaseApplicationForLogic, FormChanges, FormData, FormForApplicantForUI, FormForApplicantFromApi, FormResponseForPdf, FormStatuses, FormTypes, MyApplicationFormUI } from '@features/configure-forms/form.typing';
import { CopyApplicationService } from '@features/copy-application/copy-application.service';
import { CyclesService } from '@features/cycles/cycles.service';
import { FormFieldHelperService } from '@features/form-fields/services/form-field-helper.service';
import { FormFieldService } from '@features/form-fields/services/form-field.service';
import { BlankSpecialHandling } from '@features/forms/form-renderer-components/standard-form-components/form-special-handling/form-special-handling.component';
import { ComponentHelperService } from '@features/forms/services/component-helper/component-helper.service';
import { FormHelperService } from '@features/forms/services/form-helper/form-helper.service';
import { LogicState } from '@features/logic-builder/logic-builder.typing';
import { NonprofitService } from '@features/nonprofit/nonprofit.service';
import { SearchAllOrgsModalComponent } from '@features/organization-search/search-all-orgs-modal/search-all-orgs-modal.component';
import { ProgramAutomationService } from '@features/programs/program-automation/program-automation.service';
import { ProgramService } from '@features/programs/services/program.service';
import { SignatureService } from '@features/signature/signature.service';
import { SupportService } from '@features/support/support.service';
import { UserService } from '@features/users/user.service';
import { AddressFormatterService, OrganizationEligibleForGivingStatus, SearchResult, SimpleStringMap } from '@yourcause/common';
import { CallMaker, CallMakerFactory } from '@yourcause/common/call-maker';
import { DATE_TIME_FORMAT, DateService } from '@yourcause/common/date';
import { HeapService, TrackingEventNames, TrackingPropertyNames } from '@yourcause/common/heap';
import { I18nService } from '@yourcause/common/i18n';
import { LogService } from '@yourcause/common/logging';
import { ConfirmActionResult, ConfirmationModalComponent, ConfirmationModalWithSecondaryActionComponent, ModalFactory, TakeActionResult } from '@yourcause/common/modals';
import { NotifierService } from '@yourcause/common/notifier';
import { SignatureModalResponse } from '@yourcause/common/signature';
import { ApplicationApplicantService } from '../application-applicant.service';
import { ApplicationAwardsModalComponent } from '../application-awards-modal/application-awards-modal.component';
import { TextFriendlySpecialCharCleaner } from '@yourcause/common/form-control-validation';
import { FormsService } from '@features/configure-forms/services/forms/forms.service';

export enum PassFailApplyTypes {
  Eligibility = 1,
  Organization = 2
}

@Component({
    selector: 'gc-apply-page',
    templateUrl: './apply-page.component.html',
    styleUrls: ['./apply-page.component.scss'],
    standalone: false
})
export class ApplyPageComponent implements OnInit, OnDestroy {
  programOrRoutingId = this.activatedRoute.snapshot.params.programId;
  applicationId = this.activatedRoute.snapshot.params.applicationId;
  formId = this.activatedRoute.snapshot.params.formId;
  organizationId = this.activatedRoute.snapshot.queryParams.organizationId;
  copyOfId = this.activatedRoute.snapshot.queryParams.copyOfId;
  submitting: boolean;
  FormTypes = FormTypes;
  FormStatuses = FormStatuses;
  ProgramApplicantType = ProgramApplicantType;
  applicant: Applicant;
  applicantAddressString: string;
  isFormDraft: boolean;
  application: ApplyPageApplication;
  _showForm: boolean;
  _showEligibilityForm: boolean;
  programNotStarted: boolean;
  formData: FormData;
  eligibilityFormData: FormData;
  selectedOrg: SearchResult;
  orgId: number;
  hideSelectOrg = false;
  eligibilityPassed = this.i18n.translate(
    'APPLY:textEligibilityPassed',
    {},
    'Eligibility Passed'
  );
  eligibilityFailed = this.i18n.translate(
    'APPLY:textQualificationsNotMet',
    {},
    'Qualifications Not Met'
  );
  instructions = '';
  isNomination = false;
  nominationForm: NominationForm;
  formStatusMap = this.statusService.formStatusMap;
  statusText = '';
  statusIcon = '';
  pendingFormChanges: FormChanges[] = [];
  nomineeFormValid: boolean;
  markForValidity: boolean;
  revisionRequested = false;
  canEditForm = false;
  ApplicationStatuses = ApplicationStatuses;
  portalTranslations = this.translationService.viewTranslations;
  formTranslationMap = this.portalTranslations.FormTranslation;
  cycleTranslations = this.portalTranslations.Grant_Program_Cycle;
  currentFormName: string;
  currentFormDescription: string;
  showDates = false;
  acceptingNewSubmissions = false;
  programAcceptanceText = '';
  programIsAcceptingText = this.i18n.translate(
    'APPLY:textProgramIsAcceptingApplications',
    {},
    'Program is accepting applications'
  );
  programIsAcceptingNominations = this.i18n.translate(
    'APPLY:textProgramIsAcceptingNominations',
    {},
    'Program is accepting nominations'
  );
  programIsNotAcceptingText = this.i18n.translate(
    'APPLY:textProgramIsNotAccepting',
    {},
    'Program is not accepting applications'
  );
  programIsNotAcceptingNominations = this.i18n.translate(
    'APPLY:textProgramIsNotAcceptingNominations',
    {},
    'Program is not accepting nominations'
  );
  formAlert: MyApplicationFormUI;
  files: ApplicationApplicantFile[] = [];
  appSubmittedDate: string;
  updatingOrg = false;
  forms: FormForApplicantForUI[] = [];
  conditionalVisibilityState: LogicState<BaseApplicationForLogic, boolean>;
  callMaker: CallMaker<FormChanges[], boolean> = CallMakerFactory.create(
    (changes) => this.handleAutoSave(changes),
    500,
    false,
    true
  );
  translations: SimpleStringMap<string> = {};
  richTextTranslations: SimpleStringMap<string> = {};
  awards: MyAward[] = [];
  awardTotalText: string;
  programTranslationMap: ProgramTranslationMap;
  program: ProgramDetail;
  DATE_TIME_FORMAT = DATE_TIME_FORMAT;

  constructor (
    private mixpanel: MixpanelService,
    private logger: LogService,
    private formFieldService: FormFieldService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private applicantService: ApplicantService,
    private router: Router,
    private modalFactory: ModalFactory,
    private spinnerService: SpinnerService,
    private notifier: NotifierService,
    private i18n: I18nService,
    private applicationApplicantService: ApplicationApplicantService,
    private applicationDownloadService: ApplicationDownloadService,
    private addressFormatter: AddressFormatterService,
    private statusService: StatusService,
    private nonprofitService: NonprofitService,
    private clientSettingsService: ClientSettingsService,
    private translationService: TranslationService,
    private cyclesService: CyclesService,
    private notifierService: NotifierService,
    private specialHandlingService: SpecialHandlingService,
    private applicationAttachmentService: ApplicationAttachmentService,
    private supportService: SupportService,
    private formHelperService: FormHelperService,
    private componentHelper: ComponentHelperService,
    private addOrgService: AddOrganizationService,
    private signatureService: SignatureService,
    private programAutomationService: ProgramAutomationService,
    private applicationFormService: ApplicationFormService,
    private awardService: AwardService,
    private formFieldHelperService: FormFieldHelperService,
    private applicationEligibilityService: ApplicationEligibilityService,
    private dateService: DateService,
    private copyApplicationService: CopyApplicationService,
    private heapService: HeapService,
    private programService: ProgramService,
    private applicantCommunicationService: ApplicantCommunicationService,
    private formService: FormsService
  ) { }

  get isRouting () {
    return this.activatedRoute.snapshot.data.isRouting;
  }

  get showForm () {
    return this._showForm;
  }

  set showForm (val) {
    this._showForm = val;
  }

  get showEligibilityForm () {
    return this._showEligibilityForm;
  }

  set showEligibilityForm (val) {
    this._showEligibilityForm = val;
  }

  async ngOnInit () {
    this.spinnerService.startSpinner();
    const result = await this.applicationApplicantService.setPortalApplication(
      this.applicationId,
      this.formId,
      this.programOrRoutingId,
      !!this.organizationId ? +this.organizationId : null,
      this.isRouting,
      this.programAutomationService.routingAppForSave,
      this.copyOfId
    );
    this.spinnerService.stopSpinner();
    this.application = result.application;
    this.forms = result.forms;
    
    this.program = this.applicationApplicantService.currentProgram;
    if (!!this.program) {
      this.programTranslationMap = this.translationService.getProgramTranslationMapForApplicant(
        this.program
      );
    }
    
    this.setHelpers();
    
    // track event
    const affiliateInfo = this.clientSettingsService.getClientAffiliateInfo(this.application.clientName);
    this.mixpanel.register({
      ...affiliateInfo,
      [TrackingPropertyNames.ClientName]: this.application.clientName,
      [TrackingPropertyNames.ProgramName]: this.application.grantProgramName
    });
    this.trackApplyPageEvent(TrackingEventNames.PageView);

    this.handleApplicantType();
  }

  setHelpers () {
    this.setMyAppsAndAwards();
    this.setApplicant();
    this.setNominationAttrs();
    this.formData = this.application.form?.formData;
    this.eligibilityFormData = this.application.eligibilityForm?.formData;
    this.isFormDraft = !this.applicationId || this.application.form.isDraft;
    this.revisionRequested = this.application.statusId === ApplicationStatuses.Hold &&
      this.application.form.isDraft &&
      this.application.form.applicationFormStatusId !== FormStatuses.Submitted;
    this.showDates = !this.application.hideCycleDatesInApplicantPortal;
    this.checkProgramCycles();
    this.setContactSupportPrepopData();
    this.setInstructions();
  }

  async setMyAppsAndAwards () {
    await Promise.all([
      this.applicationApplicantService.getMyApplications(),
      this.awardService.getMyAwards()
    ]);
    this.setFormAlertAndFiles();
    this.awards = (this.awardService.myAwards || []).filter((award) => {
      return award.applicationId === +this.applicationId;
    });
    this.awardTotalText = this.awardService.getAwardTotalText(this.awards);
  }

  getSavedSignature = async () => {
    await this.signatureService.setSignature();
    
    return this.signatureService.savedSignatureFile;
  };

  async viewGuidelines (): Promise<void> {
    const guidelinesTranslation = this.programTranslationMap?.guidelines ??
      this.application.grantProgramGuidelines;
    
    await this.modalFactory.open(
      ConfirmationModalComponent,
      {
        confirmText: guidelinesTranslation,
        modalHeader: this.i18n.translate(
          'PROGRAM:textProgramGuidelinesTitle',
          {},
          'Program Guidelines'
        ),
        useRichTextDisplay: true
      }
    );
  }

  viewAwards () {
    this.modalFactory.open(
      ApplicationAwardsModalComponent,
      {
        applicationId: this.applicationId,
        awards: this.awards
      }
    );
  }

  setApplicant () {
    this.applicant = this.userService.get('applicant');
    this.applicantAddressString =  this.addressFormatter.formatSimpleGrantsAddressToSingleLine({
      address: this.applicant.address,
      address2: this.applicant.address2,
      city: this.applicant.city,
      state: this.applicant.state,
      country: this.applicant.country,
      postalCode: this.applicant.postalCode
    });
  }

  setContactSupportPrepopData () {
    const app = this.application;
    this.supportService.setPrepopData({
      reason: null as ContactSupport.ContactReasonFullList,
      firstName: this.applicant.firstName,
      lastName: this.applicant.lastName,
      email: this.applicant.email,
      phoneNumber: this.applicant.phoneNumber,
      companySeekingGrantFrom: app.clientName,
      applicationId: app.id,
      program: app.grantProgramName,
      organization: app.organization?.name,
      ein: app.organization?.orgIdentification,
      questionDescription: ''
    });
  }

  setNominationAttrs () {
    this.isNomination = this.application.form?.formType === FormTypes.NOMINATION;
    if (this.isNomination) {
      const nominee: Nominee = this.application.nominee || ({} as Nominee);
      this.nominationForm = {
        email: nominee.email,
        firstName: (nominee.firstName || '').trim(),
        lastName: (nominee.lastName || '').trim(),
        phoneNumber: nominee.phoneNumber,
        position: nominee.position
      };
      this.nomineeFormValid = !!nominee.email &&
        !!nominee.firstName &&
        !!nominee.lastName &&
        !Validators.email(new UntypedFormControl(nominee.email));
    }
  }

  checkProgramCycles () {
    if (this.isRouting) {
      this.canEditForm = true;
    } else {
      const cycleHelpers = this.cyclesService.getCycleDateHelpers(
        this.applicationId ? [this.application.cycle] : this.application.cycles
      );
      const notSubmittedYet = this.application.statusId === ApplicationStatuses.Draft;

      if (notSubmittedYet) {
        if (this.applicationId && cycleHelpers.currentCycle) {
          this.acceptingNewSubmissions = +this.application.cycle.id === +cycleHelpers.currentCycle.id;
        } else {
          this.acceptingNewSubmissions = !!cycleHelpers.currentCycle;
        }
      }
      if (!this.acceptingNewSubmissions) {
        const isDefaultFormWithNoRevisionRequest = !this.revisionRequested &&
          this.application.form.formId === this.application.defaultFormId;
        this.canEditForm = !notSubmittedYet &&
          !this.application.isArchived &&
          !isDefaultFormWithNoRevisionRequest &&
          (
            this.revisionRequested ||
            this.application.form.applicationFormStatusId !== FormStatuses.Submitted
          );
      } else {
        this.canEditForm = this.isFormDraft;
      }

      if (notSubmittedYet) {
        if (this.acceptingNewSubmissions) {
          if (this.showDates) {
            this.programAcceptanceText = this.i18n.translate(
            this.isNomination ?
              'APPLY:textAcceptingNominationsUntilDate' :
              'APPLY:textAcceptingApplicationsUntilDate',
            {
              date: this.dateService.formatDate(cycleHelpers.currentCycle.endDate, DATE_TIME_FORMAT)
            },
            this.isNomination ?
              'Accepting nominations until __date__' :
              'Accepting applications until __date__'
          );
          } else {
            this.programAcceptanceText = this.programIsAcceptingText;
          }
        } else {
          this.programAcceptanceText = this.programIsNotAcceptingText;
        }
      }
    }
  }

  setInstructions () {
    if (this.isRouting) {
      this.instructions = this.i18n.translate(
        'common:textApplicantRoutingInstructions',
        {},
        'Fill out and submit the form on the right. Based on your responses you will be routed to a grant program where you will complete your grant application.'
      );
    } else {
      const fillOutFormInstructions = this.i18n.translate(
        'APPLY:textFillOutFormInstructions',
        {},
        'Fill out the form to your right. You can save progress and return later to submit the form.'
      );
      const alreadySubmittedInstructions = this.i18n.translate(
        'APPLY:textAlreadySubmittedForm',
        {},
        'Your form has already been submitted. You can review your responses here.'
      );
      if (this.revisionRequested) {
        this.instructions = this.i18n.translate(
          'APPLY:FORM_REQUIRES_REVIEW'
        );
      } else if (this.acceptingNewSubmissions) {
        if (this.isFormDraft) {
          if (this.showEligibilityForm) {
            this.instructions = this.i18n.translate(
              'APPLY:textCompleteEligibilityInstructions',
              {},
              'Complete the eligibility form below to access the grant application forms.'
            );
          } else if (this.showForm) {
            this.instructions = fillOutFormInstructions;
          }
        } else {
          this.instructions = alreadySubmittedInstructions;
        }
      } else if (
        this.application.statusId === ApplicationStatuses.Draft &&
        this.application.form.formType === FormTypes.REQUEST
      ) {
          this.instructions = this.i18n.translate(
            'APPLY:textProgramExpiredInstructions',
            {},
            'This application cannot be submitted because the program is no longer accepting applications.'
          );
      } else {
        if (
          this.application.statusId !== ApplicationStatuses.Draft &&
          this.isFormDraft
        ) {
          this.instructions = fillOutFormInstructions;
        } else {
          this.instructions = alreadySubmittedInstructions;
        }
      }
    }
  }

  setFormAlertAndFiles () {
    const formAlerts = this.applicationApplicantService.formsPastDue;
    this.formAlert = formAlerts.find((form) => {
      return +form.id === +this.application.applicationFormId;
    });
    const thisApp = this.applicationApplicantService.myApplications.find((app) => {
      return +app.applicationId === +this.applicationId;
    });
    this.files = thisApp?.files || [];
    const defaultForm = thisApp?.applicationForms.find((form) => {
      return form.formId === this.application.defaultFormId;
    });
    this.appSubmittedDate = defaultForm?.submissionDate;
  }

  async handleApplicantType () {
    if (this.application.programApplicantType === ProgramApplicantType.ORGS) {
      if (!this.application.organization) {
        if (!this.application.applicationId) {
          this.spinnerService.stopSpinner();
          this.showSelectOrgModal();
        } else {
          // This application was created when Org Selection was not required for the program
          this.hideSelectOrg = true;
          this.handleEligibility();
        }
      } else {
        await this.setOrganization();
        this.handleEligibility();
      }
    } else {
      this.hideSelectOrg = true;
      this.setApplicantAddressAsHandling();
      this.handleEligibility();
    }
  }

  setShowFormAttr (attr: 'showForm'|'showEligibilityForm', show: boolean) {
    this[attr] = show;
    let form;
    if (this.showForm) {
      form = this.application.form;
    } else if (this.showEligibilityForm) {
      form = this.application.eligibilityForm;
    }
    if (form) {
      this.currentFormName = form.name;
      this.currentFormDescription = form.description;
    } else {
      this.currentFormName = '';
      this.currentFormDescription = '';
    }
  }

  async setOrganization () {
    const org = this.application.organization;
    if (org) {
      let address1; let address2; let city; let stateProvRegCode; let country;
      let postalCode; let addressString; let displayName; let email; let phone;
      let parentName; let parentGuid;
      let eligibleForGivingStatusId = org.eligibleForGivingStatusId;
      const registrationId = org.orgIdentification;
      if (org.nonprofitGuid) {
        const response = await this.nonprofitService.getNonprofitAdditionalDataByGuid(
          org.nonprofitGuid
        );
        if (response.nonprofitDetail) {
          const address = this.nonprofitService.getNonprofitAddressFields(
            response.nonprofitDetail
          );
          address1 = address.address1;
          address2 = address.address2;
          city = address.city;
          stateProvRegCode = address.stateProvRegCode;
          country = address.country;
          postalCode = address.postalCode;
          addressString = this.addressFormatter.returnSingleLine(
            address
          );
          displayName = response.nonprofitDetail.displayName;
          email = response.nonprofitDetail.displayEmail;
          phone = response.nonprofitDetail.displayNumber;
          parentName = response.nonprofitDetail.parentNonprofitName;
          parentGuid = response.nonprofitDetail.parentNonprofitGuid;
          eligibleForGivingStatusId = response.nonprofitDetail.eligibleForGivingStatusId;
        }
      }

      this.setSelectedOrg(
        SearchResult.construct<SearchResult>({
          text: org.name,
          document: {
            id: '' + org.id,
            address1: address1 || org.address,
            address2: address2 || org.address2,
            city: city || org.city,
            country: country || org.country,
            iconURL: org.imageUrl,
            name: org.name,
            displayName: displayName || org.name,
            email,
            phone,
            postalCode: postalCode || org.postalCode,
            registrationId,
            isPrivateOrg: org.isPrivateOrg,
            stateProvRegCode: stateProvRegCode || org.state,
            eligibleForGivingStatusId,
            addressString: addressString || this.addressFormatter.formatSimpleGrantsAddressToSingleLine({
              address: org.address,
              address2: org.address2,
              city: org.city,
              state: org.state,
              country: org.country,
              postalCode: org.postalCode
            }),
            parentName,
            parentGuid
          }
        })
      );
    }
  }

  setSelectedOrg (org: SearchResult) {
    this.selectedOrg = org;
    if (org) {
      this.setOrgAddressAsHandling(org);
    } else {
      this.setApplicantAddressAsHandling();
    }
  }

  setOrgAddressAsHandling (org: SearchResult) {
    this.application.defaultSpecialHandling = {
      name: org.document.name || '',
      address1: org.document.address1 || '',
      address2: org.document.address2 || '',
      city: org.document.city || '',
      state: org.document.stateProvRegCode || '',
      country: org.document.country || '',
      postalCode: org.document.postalCode || '',
      notes: '',
      reason: '',
      fileUrl: ''
    };
  }

  setApplicantAddressAsHandling () {
    this.application.defaultSpecialHandling = {
      name: this.applicant.firstName + ' ' + this.applicant.lastName,
      address1: this.applicant.address || '',
      address2: this.applicant.address2 || '',
      city: this.applicant.city || '',
      state: this.applicant.state || '',
      country: this.applicant.country || '',
      postalCode: this.applicant.postalCode || '',
      notes: '',
      reason: '',
      fileUrl: ''
    };
  }

  async showSelectOrgModal (isUpdate = false) {
    this.spinnerService.startSpinner();
    const [
      favoriteOrgs,
      allowedOrgLocations
    ] = await Promise.all([
      this.applicantService.getApplicantOrganizations(),
      !this.isRouting ? this.programService.getAllowedOrgLocations(this.application.programId) : []
    ]);
    this.spinnerService.stopSpinner();
    const forceOrgSelect = !this.application.organization &&
      (this.application.programApplicantType === ProgramApplicantType.ORGS) &&
      !this.selectedOrg;

    const response = await this.modalFactory.open(
      SearchAllOrgsModalComponent,
      {
        hideAddOrg: !this.application.allowAddOrg,
        favoriteOrgs,
        processorType: this.application.isClientProcessing ? ProcessingTypes.Client : ProcessingTypes.YourCause,
        charityBucketId: this.application.charityBucketId,
        forceOrgSelect,
        clientId: this.application.clientId,
        clientName: this.application.clientName,
        orgSearchGuidelines: this.application.charityBucketDescription,
        saveNewlyCreatedOrgs: true,
        isNomination: this.isNomination,
        allowedOrgLocations,
        programId: this.application.programId,
        exitRouterLink: forceOrgSelect ? '/apply/applications' : '',
        exitLinkText: this.i18n.translate(
          forceOrgSelect ? 'GLOBAL:textGoToMyApplications' : 'common:btnCancel',
          {},
          forceOrgSelect ? 'Go to my applications' : 'Cancel'
        )
      }
    );
    if (response) {
      this.spinnerService.startSpinner();
      const passesOrgEligibility = await this.doesOrgPassProgramEligibility(response.selectedOrg);
      // Only evaluated on brand new apps
      if (passesOrgEligibility) {
        if (isUpdate) {
          this.updatingOrg = true;
        }
        await this.handleOrgSelect(response.selectedOrg, isUpdate);

        if (isUpdate) {
          await this.updateVettingStatus();
        }
      }
      this.spinnerService.stopSpinner();
    }
  }

  async doesOrgPassProgramEligibility (selectedOrg: SearchResult): Promise<boolean> {
    if (this.application.applicationId || this.isRouting) {
      return true;
    }

    const passes = await this.applicationEligibilityService.handleOrgEligibilityForMultipleApps(
      this.application.programId,
      this.application.cycle.id,
      +selectedOrg.document.id,
      this.application.multipleApplicationsLimitType,
      this.application.programApplicantType
    );
    if (!passes) {
      this.showPassFailModal(false, PassFailApplyTypes.Organization);
    }

    return passes;
  }

  async updateVettingStatus () {
    const vettingStatus = await this.applicationApplicantService.getLatestVettingStatusForApplication(
      this.applicationId,
      this.application.form.formId
    );
    this.application.latestVettingRequestStatusForOrg = vettingStatus;
  }

  async handleOrgSelect (
    org: SearchResult,
    isUpdate: boolean
  ) {
    if (isUpdate) {
      this.application = {
        ...this.application,
        specialHandling: BlankSpecialHandling
      };
    }
    this.setSelectedOrg(org);
    if (
      this.selectedOrg.document.eligibleForGivingStatusId !== OrganizationEligibleForGivingStatus.ELIGIBLE
    ) {
      this.application.latestVettingRequestStatusForOrg = null;
    }
    this.triggerApplicationChange();
    if (!isUpdate) {
      this.handleEligibility();
    }
  }

  triggerApplicationChange () {
    // We need to trigger a bogus change in order to force the application to update. Designation field can be present or absent on the form and this will safely save the existing value
    this.onChange([{
      key: 'designation',
      type: 'designation',
      isReferenceField: false,
      value: this.application.designation
    }]);
  }

  handleEligibility () {
    const formIsEligibility = this.application?.form?.formType === FormTypes.ELIGIBILITY;
    if (
      (!this.applicationId || formIsEligibility) &&
      this.application.eligibilityForm
    ) {
      if (this.canEditForm) {
        // Reference fields should start out empty when filling out eligibility
        this.application.referenceFields = {};
      }
      this.setShowFormAttr('showEligibilityForm', true);
    } else {
      this.setShowFormAttr('showForm', true);
    }
    if (this.showForm) {
      if (!this.isRouting) {
        this.callMaker.invoke(() => this.pendingFormChanges);
      }
    }
  }

  async changeFormAsync (form: FormForApplicantForUI) {
    this.formId = form.formId;
    this.spinnerService.startSpinner();
    await this.applicationApplicantService.preparePortalApplication(
        this.application.applicationId,
        this.formId,
        this.application.programId.toString()
    );
    await this.router.navigate(
      [`/apply/application/${this.applicationId}/forms/${form.formId}`]
    );
    this.setShowFormAttr('showForm', false);
    this.setShowFormAttr('showEligibilityForm', false);
    this.spinnerService.stopSpinner();
    await this.ngOnInit();
  }

  handleErrorSave (
    e: Error,
    isEligibility = false,
    isSubmit = false
  ) {
    if (isSubmit) {
      this.notifier.error(this.i18n.translate(
        isEligibility ?
          'APPLY:textErrorSubmittingEligibility' :
          'APPLY:textErrorSubmittingApplication',
      {},
      isEligibility ?
        'There was an error submitting eligibility' :
        'There was an error submitting the application'
    ));
    } else {
      this.notifier.error(this.i18n.translate(
        isEligibility ?
          'APPLY:textErrorSavingEligibility' :
          'APPLY:textErrorSavingApplication',
        {},
        isEligibility ?
          'There was an error submitting eligibility' :
          'There was an error submitting the application'
      ));
    }
    this.logger.error(e);
    this.spinnerService.stopSpinner();
  }

  nominationFormChanged (form: NominationForm) {
    this.nominationForm = form;
    this.application.nominee = {
      email: form.email,
      firstName: form.firstName,
      lastName: form.lastName,
      phoneNumber: form.phoneNumber,
      position: form.position
    };
    this.setFormData(this.formData);
    this.triggerApplicationChange();
  }

  onNomineeValidityChange (valid: boolean) {
    this.nomineeFormValid = valid;
  }

  goToMyApplications () {
    this.router.navigate(['/apply/applications']);
  }

  setFormData (data: FormData) {
    if (this.canEditForm) {
      if (this.showForm) {
        this.formData = data;
      } else if (this.showEligibilityForm) {
        this.eligibilityFormData = data;
      }
    }
  }

  async openCommunicationFlyout (comm: CommunicationForApplicant) {
    this.spinnerService.startSpinner();
    await this.applicationApplicantService.getMyApplications();
    const foundApp = this.applicationApplicantService.myApplications.find((app) => {
      return +app.applicationId === +this.application.applicationId;
    })
    this.spinnerService.stopSpinner();
    this.applicantCommunicationService.openCommunicationFlyout(comm, foundApp);
  }

  onChange (changes: FormChanges[] = []) {
    if (this.canEditForm) {
      if (this.showForm) {
        this.pendingFormChanges = [
          ...this.pendingFormChanges,
          ...changes
        ];
        if (!this.isRouting) {
          this.callMaker.invoke(() => this.pendingFormChanges);
        }
      }
    }
  }

  async handleAutoSave (changes: FormChanges[]) {
    this.setFloaterText();
    const response = this.formFieldHelperService.handleChangeTracking(
      changes,
      this.application as Partial<BaseApplication>
    );
    const appNeedsUpdated = response.appNeedsUpdated;
    const refChangeTracker = response.refChangeTracker;
    if (!this.applicationId) {
      // Scenario where app has not been created yet
      this.spinnerService.startSpinner();
      await this.doSave(true, false, false, refChangeTracker);
      this.spinnerService.stopSpinner();
    } else {
      if (Object.keys(refChangeTracker).length > 0) {
        await this.saveRefFields(refChangeTracker);
      }
      if (appNeedsUpdated || this.updatingOrg) {
        await this.doSave(true, false, false, null, null, this.updatingOrg);
        this.updatingOrg = false;
        // we set org ID here so that app is updated and then report fields can be refetched
        this.orgId = +this.selectedOrg?.document?.id;
      }
    }

    this.pendingFormChanges = this.pendingFormChanges.filter(change => {
      return !changes.includes(change);
    });
    this.setFloaterText(true);

    return true;
  }

  async submitRouting () {
    const response = this.formFieldHelperService.handleChangeTracking(
      this.pendingFormChanges,
      this.application as Partial<BaseApplication>
    );
    const changes = this.formFieldService.adaptFormChangesForSave(
      response.refChangeTracker,
      null,
      null,
      null,
      false,
      false
    );

    // track event
    this.trackApplyPageEvent(TrackingEventNames.SubmitForm);
    
    await this.programAutomationService.submitProgramRouting(
      this.application.clientId,
      this.application.referenceFields,
      this.programOrRoutingId,
      {
        formId: this.application.form.formId,
        formRevisionId: this.application.form.formRevisionId,
        formResponse: {
          amountRequested: this.application.amountRequestedForEdit || 0,
          currencyRequested: this.application.currencyRequested,
          referenceFields: changes.standardChangeValues.map((change) => {
            return {
              id: change.referenceFieldId,
              value: change.value
            };
          })
        }
      },
      this.selectedOrg,
      this.isNomination
    );
  }

  async submitForm (signatureResponse: SignatureModalResponse) {
    let signatureFileId: number;
    if (this.application.form.requireSignature) {
      signatureFileId = await this.signatureService.handleSignatureModalResponse(signatureResponse);
      if (!signatureFileId) {
        // If no file ID, signature failed to upload
        return;
      }
    }
    /* Attempt to save all reference fields before submission */
    if (!this.submitting) {
      if (this.isNomination && !this.nomineeFormValid) {
        this.showForm = false;
        this.markForValidity = true;
        this.notifierService.error(this.i18n.translate(
          'APPLY:textFormIsInvalidFixErrorsBelow',
          {},
          'Form is invalid. Please fix the errors below.'
        ));
        // Form thinks submit was successful so it hides the form / submit button.
        // Set timeout will re-render and force it to show.
        setTimeout(() => {
          this.showForm = true;
        });
      } else {
        this.spinnerService.startSpinner();
        this.submitting = true;
        this.canEditForm = false;
        const errorOnSave = await this.doSave(
          false,
          true,
          false,
          undefined,
          signatureFileId
        );
        this.submitting = false;
        if (!errorOnSave) {
          try {
            this.logger.log('Begin PDF Snapshot', {
              client: this.application.clientName,
              applicationId: '' + this.application.applicationId,
              applicationFormId: '' + this.application.applicationFormId
            });
            this.application.statusId = await this.applicationApplicantService.getApplicationStatus(
              this.application.applicationId,
              this.application.form.formId
            );
            await this.downloadPDF(true);
          } catch (e) {
            this.logger.log('PDF Snapshot Failed', {
              client: this.application.clientName,
              applicationId: '' + this.application.applicationId,
              applicationFormId: '' + this.application.applicationFormId
            });
          }
          this.notifier.success(this.i18n.translate(
            this.isNomination ?
              'APPLY:textSuccessfullySubmittedNomination' :
              'APPLY:textSuccessfullySubmittedApp',
            {},
            this.isNomination ?
              'Successfully submitted the nomination' :
              'Successfully submitted the application'
          ));
          
          // track event
          this.trackApplyPageEvent(TrackingEventNames.SubmitForm);
          
          this.applicationApplicantService.setMyApplications(undefined);
          this.goToMyApplications();
        } else {
          this.canEditForm = true;
        }
        this.spinnerService.stopSpinner();
      }
    }
  }

  async returnOrgInfoForSave (isUpdateOrg = false) {
    let needToHandleOrgAfterSave = false;
    let orgId;
    if (!!this.selectedOrg) {
      if (!this.selectedOrg.document.id) {
        orgId = await this.addOrgService.createPrivateOrg(this.applicant.id, this.application.clientId);
      } else {
        orgId = +this.selectedOrg.document.id;
      }
      // We can only call AddOrVetOrganization if application id exists,
      // so check if we need to call this function again
      needToHandleOrgAfterSave = !this.applicationId;
      orgId = await this.addOrgService.handleOrgForApplication(
        this.application.cycle?.id,
        this.application.isClientProcessing ?
          ProcessingTypes.Client :
          ProcessingTypes.YourCause,
        orgId,
        this.applicationId,
        this.selectedOrg.document.eligibleForGivingStatusId,
        this.application.latestVettingRequestStatusForOrg,
        this.application.clientId,
        '',
        '',
        '',
        isUpdateOrg
      );
      if (isUpdateOrg) {
        this.applicationApplicantService.getMyApplications(true);
      }
      this.updateOrgId(orgId);
    }

    return { orgId, needToHandleOrgAfterSave };
  }

  updateOrgId (orgId: number) {
    // this could come back null and we don't want to stringify
    if (!!orgId) {
      this.selectedOrg.document.id = '' + orgId;
    } else {
      this.selectedOrg.document.id = null;
    }
  }

  async saveRefFields (
    refChangeTracker: ReferenceFieldsUI.RefResponseMap
  ): Promise<boolean> {
    const response = this.formFieldService.adaptFormChangesForSave(
      refChangeTracker,
      this.application.applicationFormId,
      this.applicationId,
      this.application.form.formRevisionId,
      false,
      false
    );
    const passResponse = await this.applicationFormService.handleSaveOfChangedFields(
      response,
      this.applicationId,
      this.application.form.formId,
      this.application.form.formRevisionId,
      this.application.applicationFormId,
      false,
      false
    );

    return passResponse.standardPassed && passResponse.tablePassed;
  }

  async doSave (
    isDraft = true,
    force = false,
    refreshVettingStatus = false,
    refResponseMap?: ReferenceFieldsUI.RefResponseMap,
    userSignatureId?: number,
    updatingOrg = false
  ): Promise<boolean> {
    if ((this.canEditForm && !this.submitting) || force) {
      let errorOnSave = false;
      try {
        const { orgId, needToHandleOrgAfterSave } = await this.returnOrgInfoForSave(updatingOrg);
        const specialHandling = this.specialHandlingService.getSpecialHandlingForSave(
          this.application.specialHandling,
          this.application.defaultSpecialHandling
        );
        const specialHandlingValid = this.specialHandlingService.isSpecialHandlingValid(
          this.application.specialHandling
        );

        const {
          amountRequestedRequired,
          careOfRequired,
          paymentDesignationRequired
        } = this.formHelperService.getRequiredStandardFields(
          this.application.form.formDefinition
        );

        let data: SaveApplication = {
          ...specialHandling,
          applicationId: this.applicationId,
          amountRequested: this.application.amountRequestedForEdit || 0,
          currencyRequested: this.application.currencyRequested,
          grantProgramId: this.application.programId,
          isDraft,
          form: {
            formId: this.application.form.formId,
            formRevisionId: this.application.form.formRevisionId,
            formData: this.formData || {},
            isDraft
          },
          organizationId: orgId,
          paymentDesignation: this.application.designation ?
            TextFriendlySpecialCharCleaner(this.application.designation) : '',
          careOf: this.application?.careOf,
          inKindAmountRequested: (this.application.inKindItems || []).filter((item) => {
            return !!item.itemIdentification && +item.count > 0;
          }),
          requiredReferenceFieldKeys: this.formHelperService.getRequiredReferenceFieldKeys(
            this.conditionalVisibilityState,
            this.application.form.formDefinition
          ),
          amountRequestedRequired,
          careOfRequired,
          paymentDesignationRequired,
          userSignatureId,
          userSignatureBypassed: false
        };
        if (this.isNomination) {
          data.nominee = {
            ...this.nominationForm,
            orgId
          };
        }
        let copyResult: TakeActionResult<ApplicationEligibility.CopyApplicationResponse>;
        let copyFailed = false;
        if (!!this.copyOfId && !this.applicationId) {
          copyResult = await this.copyApplicationService.proceedWithCopy(
            this.copyOfId,
            this.isNomination,
            false,
            this.application.clientId
          );
          if (!copyResult.passed) {
            // Copy failed, send back to applications page
            copyFailed = true;
            this.goToMyApplications();
          } else {
            await this.applicationApplicantService.preparePortalApplication(
              copyResult.endpointResponse.applicationId,
              this.application.form.formId,
              '' + this.application.programId
            );
            const currentApp = this.applicationApplicantService.currentApp;
            data = {
              ...data,
              amountRequested: currentApp.currencyRequestedAmountEquivalent,
              currencyRequested: currentApp.currencyRequested,
              inKindAmountRequested: currentApp.inKindItems,
              paymentDesignation: currentApp.paymentDesignation,
              careOf: currentApp.careOf,
              specialHandlingName: currentApp.specialHandlingName,
              specialHandlingAddress1: currentApp.specialHandlingAddress1,
              specialHandlingAddress2: currentApp.specialHandlingAddress2,
              specialHandlingCity: currentApp.specialHandlingCity,
              specialHandlingStateProvinceRegion: currentApp.specialHandlingStateProvinceRegion,
              specialHandlingCountry: currentApp.specialHandlingCountry,
              specialHandlingPostalCode: currentApp.specialHandlingPostalCode,
              specialHandlingNotes: currentApp.specialHandlingNotes,
              specialHandlingReason: currentApp.specialHandlingReason,
              specialHandlingFileUrl: currentApp.specialHandlingFileUrl
            };
          }
        }
        if (specialHandlingValid && !copyFailed) {
          let response: SaveApplicationResponse;
          if (!copyResult) {
            if (!isDraft) {
              this.logger.log('Start Submit Form', {
                client: this.application.clientName,
                applicationId: '' + this.application.applicationId,
                applicationFormId: '' + this.application.applicationFormId
              });
            }
            response = await this.applicationApplicantService.saveApplication(data);
            if (!isDraft) {
              this.logger.log('Finish Submit Form', {
                client: this.application.clientName,
                applicationId: '' + this.application.applicationId,
                applicationFormId: '' + this.application.applicationFormId
              });
            }
            this.application.currencyRequestedAmountEquivalent = response.currencyRequestedAmountEquivalent;
          }
          const newAppId = response?.id || copyResult.endpointResponse.applicationId;
          if (!this.applicationId && newAppId) {
            await this.handleInitialSave(
              newAppId,
              response?.applicationFormId || copyResult.endpointResponse.applicationFormId,
              needToHandleOrgAfterSave,
              refreshVettingStatus,
              refResponseMap,
              data
            );
          }
        }
      } catch (e) {
        this.handleErrorSave(e as Error, false, !isDraft);
        errorOnSave = true;
      }

      return errorOnSave;
    }

    return false;
  }

  async handleInitialSave (
    newId: number,
    applicationFormId: number,
    needToHandleOrgAfterSave = false,
    refreshVettingStatus = false,
    refChangeTracker: ReferenceFieldsUI.RefResponseMap,
    savePayload: SaveApplication
  ) {    
    // We are going to navigate to the new app, so call maker can stop and re-init after
    this.callMaker.complete();
    this.translationService.resetViewTranslations(false);
    // reset my applications so new app shows on home page
    this.applicationApplicantService.getMyApplications(true);
    if (
      !!this.programAutomationService.routingAppForSave &&
      this.application.programId === this.programAutomationService.routingAppForSave.programId
    ) {
      // If we were routed here by program automation, tie the responses to the new application
      await this.applicationApplicantService.saveApplicantRoutingOrEligibilityResponsesToApp(
        newId,
        savePayload,
        this.programAutomationService.routingAppForSave
      );
      this.programAutomationService.clearRoutingAppForSave();
    } else if (!!this.applicationApplicantService.eligibilityResponseToSave) {
      // If the applicant completed eligibility, tie the response to the new application
      await this.applicationApplicantService.saveApplicantRoutingOrEligibilityResponsesToApp(
        newId,
        savePayload,
        this.applicationApplicantService.eligibilityResponseToSave
      );
      this.applicationApplicantService.clearEligibilityResponseToSave();
    }
    this.applicationId = newId;
    this.application = {
      ...this.application,
      applicationFormId
    };
    if (needToHandleOrgAfterSave) {
      await this.returnOrgInfoForSave();
    }
    if (refreshVettingStatus) {
      await this.updateVettingStatus();
    }
    if (refChangeTracker && Object.keys(refChangeTracker).length > 0) {
      await this.saveRefFields(refChangeTracker);
    }
    this.navigateToApplication();
  }

  private setFloaterText (toSaved = false) {
    if (toSaved) {
      this.statusText = this.i18n.translate('GLOBAL:textSaved');
      this.statusIcon = 'check-circle';
    } else {
      this.statusText = this.i18n.translate('GLOBAL:textSaving');
      this.statusIcon = 'ellipsis-h-alt';
    }
  }

  async openFile (file: ApplicationApplicantFile) {
    await this.applicationAttachmentService.handleDownloadFile(
      file.fileUploadId,
      null,
      null,
      file.fileName
    );
  }

  async saveEligibility () {
    if (!this.submitting) {
      this.spinnerService.startSpinner();
      this.submitting = true;
      const formData: FormData = {};
      this.application.eligibilityForm.formDefinition.forEach((tab) => {
        this.componentHelper.eachComponent(tab.components, (comp) => {
          const refKey = this.componentHelper.getRefFieldKeyFromCompType(
            comp.type
          );
          if (!!refKey) {
            formData[comp.key] = this.application.referenceFields[refKey];
          } else if (this.componentHelper.isEmployeeSsoComponent(comp.type)) {
            const attr = this.componentHelper.getEmployeeSsoAttrFromCompType(comp.type);
            formData[comp.key] = this.application.employeeInfo[attr];
          } else {
            formData[comp.key] = (this.application as any)[comp.type];
          }
        });
      });
      const { orgId } = await this.returnOrgInfoForSave();
      const data: SubmitEligibility = {
        id: this.applicationId,
        formRevisionId: this.application.eligibilityForm.formRevisionId,
        grantProgramId: this.application.programId,
        isDraft: false,
        formData: this.componentHelper.removeContentFromFormData(
          formData,
          this.application.eligibilityForm.formDefinition
        ),
        organizationId: orgId
      };
      let response;
      try {
        response = await this.applicationApplicantService.submitEligibility(data);
      } catch (e) {
        this.handleErrorSave(e as Error, true, true);
      }
      this.submitting = false;
      this.spinnerService.stopSpinner();
      if (!!response) {
        this.showPassFailModal(response.matchesMasterResponse);
      }
    }
  }

  async showPassFailModal (
    passed: boolean,
    passFailType = PassFailApplyTypes.Eligibility
  ) {
    const deps = this.getPassFailModalAttrs(passed, passFailType);
    const modalAction = await this.modalFactory.open(
      ConfirmationModalWithSecondaryActionComponent,
      deps,
      { class: 'modal-small' }
    );
    switch (passFailType) {
      case PassFailApplyTypes.Eligibility:
        if (modalAction === ConfirmActionResult.Primary) {
          if (passed) {
            this.spinnerService.startSpinner();
            const eligibilityData: RoutingOrEligibilityChangesToSave = {
              amountRequested: this.application.amountRequestedForEdit,
              currencyRequested: this.application.currencyRequested,
              formId: this.application.eligibilityForm.formId,
              formRevisionId: this.application.eligibilityForm.formRevisionId,
              referenceFields: this.application.referenceFields
            };
            this.applicationApplicantService.setEligibilityResponseToSave(eligibilityData);
            this.application = {
              ...this.application,
              referenceFields: {}
            };
            this.spinnerService.stopSpinner();
            if (!this.isRouting) {
              this.callMaker.invoke(() => this.pendingFormChanges);
            }
          } else {
            this.goToMyApplications();
          }
        } else if (modalAction === ConfirmActionResult.Secondary) {
          this.setShowFormAttr('showEligibilityForm', false);
          this.eligibilityFormData = {};
          setTimeout(() => {
            this.setShowFormAttr('showEligibilityForm', true);
          }, 100);
        } else {
          this.goToMyApplications();
        }
        break;
      case PassFailApplyTypes.Organization:
        this.goToMyApplications();
        break;
    }
  }

  getPassFailModalAttrs (
    passed: boolean,
    passFailType: PassFailApplyTypes
  ) {
    const base = {
      primaryCustomColor: this.application.clientBranding.brandPrimary,
      secondaryCustomColor: this.application.clientBranding.brandSecondary,
      customHeaderFontColor: 'white',
      confirmButtonText: passed ? this.i18n.translate(
        !!this.copyOfId ? 'common:textProceedWithCopyApplication' : 'APPLY:textContinueToApp',
        {},
        !!this.copyOfId ? 'Proceed with copy application' : 'Continue to application'
      ) : this.i18n.translate(
        'GLOBAL:textGoToMyApplications',
        {},
        'Go to my applications'
      ),
      useRichTextDisplay: true
    };
    switch (passFailType) {
      case PassFailApplyTypes.Eligibility:
        return {
          ...base,
          modalHeader: passed ? this.eligibilityPassed : this.eligibilityFailed,
          confirmText: passed ? this.application.passMessage : this.application.failMessage,
          secondaryButtonText: passed ? null : this.i18n.translate(
            'APPLY:textStartOver',
            {},
            'Start over'
          )
        };
      case PassFailApplyTypes.Organization:
        // Passed is always true in this scenario

        return {
          ...base,
          modalHeader: this.i18n.translate(
            'GLOBAL:hdrOrganizationNotEligible',
            {},
            'Organization Not Eligible'
          ),
          confirmText: this.applicationEligibilityService.getOrgEligibilityMessageforMultipleApps(
            this.application.multipleApplicationsLimitType,
            this.application.multipleApplicationsLimitMessage,
            this.isNomination
          )
        };
    }
  }

  navigateToApplication () {
    this.formId = this.application.form.formId;
    const route = `/apply/application/${this.applicationId}/forms/${this.formId}`;
    this.router.navigate([route]);
  }

  async downloadPDF (isForFormSubmission: boolean) {
    let orgInfo: OrgInfoForPDF;
    if (!!this.selectedOrg) {
      orgInfo = {
        name: this.selectedOrg.document.name,
        addressString: this.selectedOrg.document.addressString,
        registrationId: this.selectedOrg.document.registrationId ?? '',
        phone: this.selectedOrg.document.phone
      };
    }
    const form = this.showForm ?
      this.application.form :
      this.application.eligibilityForm;
    let foundForm: FormForApplicantFromApi;
    if (form) {
      foundForm = this.forms.find((f) => f.formId === form.formId);
    }
    const currentResponse: FormResponseForPdf = {
      applicationFormId: form.applicationFormId,
      formRevisionId: form.formRevisionId,
      applicationFormStatusId: !isForFormSubmission ? form.applicationFormStatusId : FormStatuses.Submitted,
      formDefinition: form.formDefinition,
      formData: form.formData,
      submittedDate: foundForm ? foundForm.submittedDate : '',
      createdBy: null,
      submittedBy: null,
      decision: null,
      reviewerRecommendedFundingAmount: null
    };
    if (!this.appSubmittedDate && isForFormSubmission) {
      this.appSubmittedDate = new Date().toDateString();
    }
    await this.applicationDownloadService.downloadFormPDF(
      this.application,
      orgInfo,
      currentResponse,
      form,
      this.formService.getAudienceFromFormType(form.formType),
      this.appSubmittedDate,
      this.isNomination,
      this.application.nominee,
      false,
      this.application.clientName,
      this.translations,
      this.richTextTranslations,
      isForFormSubmission,
      isForFormSubmission ? this.application.applicationFormId: null,
      !isForFormSubmission && this.awards.length > 0 ?
        this.awardService.constructAwards(this.awards) :
        null
    );
  }
  
  trackApplyPageEvent (eventName: string) {
    const isDefaultForm = this.application.form.formId === this.application.defaultFormId;
    this.heapService.track(
      eventName,
      {
        [TrackingPropertyNames.ClientId]: this.application.clientId,
        [TrackingPropertyNames.ClientName]: this.application.clientName,
        [TrackingPropertyNames.ProgramId]: this.application.programId,
        [TrackingPropertyNames.ProgramName]: this.application.grantProgramName,
        [TrackingPropertyNames.FormId]: this.application.formId,
        [TrackingPropertyNames.FormName]: this.application.form.name,
        [TrackingPropertyNames.IsDefault]: String(isDefaultForm)
      }
    );
  }

  ngOnDestroy () {
    this.mixpanel.unregister(TrackingPropertyNames.ProgramName);
    this.mixpanel.unregister(TrackingPropertyNames.ClientName);
    this.applicationApplicantService.clearClientBranding();
  }
}
